import React, { useState, useEffect } from 'react';
import { salesAPI, productsAPI } from '../backend';
import { TrendingUp, Clock, DollarSign, ShoppingBag, BarChart2, AlertCircle } from 'lucide-react';
import { brandLabel } from '../utils/brands';

function StatCard({ icon, label, value, sub, accent }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: 'green' | 'red' | 'yellow' | 'blue';
}) {
  return (
    <div className={`fin-card fin-card--${accent || 'blue'}`}>
      <div className="fin-card-icon">{icon}</div>
      <div className="fin-card-body">
        <div className="fin-card-label">{label}</div>
        <div className="fin-card-value">{value}</div>
        {sub && <div className="fin-card-sub">{sub}</div>}
      </div>
    </div>
  );
}

function Financials() {
  const [sales, setSales] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'all' | '30' | '7'>('30');
  const [activeTab, setActiveTab] = useState<'resumen' | 'analiticas'>('resumen');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [s, p] = await Promise.all([salesAPI.getAll(), productsAPI.getAll()]);
        setSales(s);
        setProducts(p);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner" />
      <p>Cargando financieros...</p>
    </div>
  );

  if (error) return <div className="error">Error: {error}</div>;

  // Filter by period
  const now = new Date();
  const filteredSales = sales.filter(s => {
    if (period === 'all') return true;
    const days = Number(period);
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - days);
    return new Date(s.created_at) >= cutoff;
  });

  // Build a cost lookup from products
  const costMap: Record<number, number> = {};
  products.forEach(p => { costMap[p.id] = p.cost ?? 0; });

  // Free samples carry no revenue but do carry real cost — keep them out of
  // revenue/margin/ticket metrics so they don't look like underperforming sales.
  const sampleSales = filteredSales.filter(s => s.sale_type === 'Muestra');
  const realSales = filteredSales.filter(s => s.sale_type !== 'Muestra');

  // Core metrics
  const totalRevenue = realSales.reduce((sum, s) => sum + (s.total ?? 0), 0);

  const collectedRevenue = realSales.reduce((sum, s) => {
    const saleProducts = s.sale_products || [];
    const paid = saleProducts.reduce((a: number, sp: any) => a + sp.price * (sp.quantity_paid ?? 0), 0);
    return sum + paid;
  }, 0);

  const pendingSales = realSales.filter(s => s.payment_status === 'Por pagar');
  const pendingAmount = pendingSales.reduce((sum, s) => {
    const saleProducts = s.sale_products || [];
    const paid = saleProducts.reduce((a: number, sp: any) => a + sp.price * (sp.quantity_paid ?? 0), 0);
    return sum + ((s.total ?? 0) - paid);
  }, 0);

  const totalCost = realSales.reduce((sum, s) => {
    const saleProducts = s.sale_products || [];
    return sum + saleProducts.reduce((a: number, sp: any) => {
      const cost = sp.product?.cost ?? costMap[sp.product_id] ?? 0;
      return a + cost * sp.quantity;
    }, 0);
  }, 0);

  const grossProfit = totalRevenue - totalCost;
  const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

  const avgSaleValue = realSales.length > 0 ? totalRevenue / realSales.length : 0;

  const sampleCost = sampleSales.reduce((sum, s) => {
    const saleProducts = s.sale_products || [];
    return sum + saleProducts.reduce((a: number, sp: any) => {
      const cost = sp.product?.cost ?? costMap[sp.product_id] ?? 0;
      return a + cost * sp.quantity;
    }, 0);
  }, 0);
  const sampleUnits = sampleSales.reduce((sum, s) => sum + (s.sale_products || []).reduce((a: number, sp: any) => a + sp.quantity, 0), 0);

  // Low stock products (stock <= 5)
  const lowStockProducts = products.filter(p => p.stock <= 5);

  // Inventory value at cost
  const inventoryValue = products.reduce((sum, p) => sum + (p.cost ?? 0) * p.stock, 0);
  const inventoryRetailValue = products.reduce((sum, p) => sum + p.price * p.stock, 0);

  // Top product by revenue in period (samples excluded — they generate $0 revenue)
  const productRevenue: Record<string, number> = {};
  realSales.forEach(s => {
    (s.sale_products || []).forEach((sp: any) => {
      const name = sp.product?.name ?? `Producto ${sp.product_id}`;
      productRevenue[name] = (productRevenue[name] ?? 0) + sp.price * sp.quantity;
    });
  });
  const topProducts = Object.entries(productRevenue)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Sales grouped by brand (samples excluded, same convention as everything above)
  const brandOf = (sp: any) =>
    brandLabel(sp.product?.brand ?? products.find(p => p.id === sp.product_id)?.brand);

  const brandSales: Record<string, { revenue: number; units: number; cost: number }> = {};
  realSales.forEach(s => {
    (s.sale_products || []).forEach((sp: any) => {
      const brand = brandOf(sp);
      if (!brandSales[brand]) brandSales[brand] = { revenue: 0, units: 0, cost: 0 };
      brandSales[brand].revenue += sp.price * sp.quantity;
      brandSales[brand].units += sp.quantity;
      brandSales[brand].cost += (sp.product?.cost ?? costMap[sp.product_id] ?? 0) * sp.quantity;
    });
  });
  const salesByBrand = Object.entries(brandSales)
    .map(([brand, v]) => ({
      brand,
      ...v,
      profit: v.revenue - v.cost,
      margin: v.revenue > 0 ? ((v.revenue - v.cost) / v.revenue) * 100 : 0
    }))
    .sort((a, b) => b.revenue - a.revenue);

  // Inventory grouped by brand (from products, independent of sales)
  const brandInventory: Record<string, { products: number; stock: number; costValue: number; retailValue: number }> = {};
  products.forEach(p => {
    const brand = brandLabel(p.brand);
    if (!brandInventory[brand]) brandInventory[brand] = { products: 0, stock: 0, costValue: 0, retailValue: 0 };
    brandInventory[brand].products += 1;
    brandInventory[brand].stock += p.stock;
    brandInventory[brand].costValue += (p.cost ?? 0) * p.stock;
    brandInventory[brand].retailValue += p.price * p.stock;
  });
  const inventoryByBrand = Object.entries(brandInventory)
    .map(([brand, v]) => ({ brand, ...v }))
    .sort((a, b) => b.costValue - a.costValue);

  // Top clients
  const clientStats: Record<string, { total: number; count: number }> = {};
  realSales.forEach(s => {
    const name = s.client?.name ?? `Cliente ${s.client_id}`;
    if (!clientStats[name]) clientStats[name] = { total: 0, count: 0 };
    clientStats[name].total += s.total ?? 0;
    clientStats[name].count += 1;
  });
  const topClients = Object.entries(clientStats)
    .map(([name, { total, count }]) => ({ name, total, count, avg: total / count }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  // Clients with highest outstanding debt
  const clientDebt: Record<string, { name: string; debt: number; sales: number }> = {};
  realSales.forEach(s => {
    const name = s.client?.name ?? `Cliente ${s.client_id}`;
    const paid = (s.sale_products || []).reduce((a: number, sp: any) => a + sp.price * (sp.quantity_paid ?? 0), 0);
    const debt = (s.total ?? 0) - paid;
    if (debt <= 0) return;
    if (!clientDebt[name]) clientDebt[name] = { name, debt: 0, sales: 0 };
    clientDebt[name].debt += debt;
    clientDebt[name].sales += 1;
  });
  const debtClients = Object.values(clientDebt)
    .sort((a, b) => b.debt - a.debt)
    .slice(0, 10);

  const fmt = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="main-view">
      <div className="sales-header">
        <h1>Financieros</h1>
        <p>Resumen financiero del negocio</p>
      </div>

      {/* Tabs */}
      <div className="fin-tabs">
        <button
          className={`fin-tab${activeTab === 'resumen' ? ' active' : ''}`}
          onClick={() => setActiveTab('resumen')}
        >
          Resumen
        </button>
        <button
          className={`fin-tab${activeTab === 'analiticas' ? ' active' : ''}`}
          onClick={() => setActiveTab('analiticas')}
        >
          Analíticas
        </button>
      </div>

      {/* Period selector */}
      <div className="fin-period-bar">
        {(['7', '30', 'all'] as const).map(p => (
          <button
            key={p}
            className={`fin-period-btn${period === p ? ' active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p === '7' ? 'Últimos 7 días' : p === '30' ? 'Últimos 30 días' : 'Todo'}
          </button>
        ))}
      </div>

      {activeTab === 'resumen' && (
        <>
          {/* Revenue & Collections */}
          <h2 className="fin-section-title">Ingresos</h2>
          <div className="fin-grid">
            <StatCard
              icon={<DollarSign size={22} />}
              label="Ventas totales"
              value={fmt(totalRevenue)}
              sub={`${realSales.length} ventas${sampleSales.length > 0 ? ` · +${sampleSales.length} muestras` : ''}`}
              accent="blue"
            />
            <StatCard
              icon={<TrendingUp size={22} />}
              label="Cobrado"
              value={fmt(collectedRevenue)}
              sub={`${totalRevenue > 0 ? ((collectedRevenue / totalRevenue) * 100).toFixed(1) : 0}% del total`}
              accent="green"
            />
            <StatCard
              icon={<Clock size={22} />}
              label="Por cobrar"
              value={fmt(pendingAmount)}
              sub={`${pendingSales.length} ventas pendientes`}
              accent="yellow"
            />
            <StatCard
              icon={<ShoppingBag size={22} />}
              label="Ticket promedio"
              value={fmt(avgSaleValue)}
              sub="por venta"
              accent="blue"
            />
          </div>

          {/* Margins */}
          <h2 className="fin-section-title">Márgenes</h2>
          <div className="fin-grid">
            <StatCard
              icon={<TrendingUp size={22} />}
              label="Ganancia bruta"
              value={fmt(grossProfit)}
              sub={`Costo total: ${fmt(totalCost)}`}
              accent={grossProfit >= 0 ? 'green' : 'red'}
            />
            <StatCard
              icon={<BarChart2 size={22} />}
              label="Margen bruto"
              value={`${grossMargin.toFixed(1)}%`}
              sub="(ingresos − costos) / ingresos"
              accent={grossMargin >= 40 ? 'green' : grossMargin >= 20 ? 'yellow' : 'red'}
            />
            {sampleSales.length > 0 && (
              <StatCard
                icon={<AlertCircle size={22} />}
                label="Costo de muestras"
                value={fmt(sampleCost)}
                sub={`${sampleSales.length} muestras · ${sampleUnits} unidades regaladas`}
                accent="yellow"
              />
            )}
          </div>

          {/* Inventory */}
          <h2 className="fin-section-title">Inventario</h2>
          <div className="fin-grid">
            <StatCard
              icon={<DollarSign size={22} />}
              label="Valor en inventario (costo)"
              value={fmt(inventoryValue)}
              sub={`Valor retail: ${fmt(inventoryRetailValue)}`}
              accent="blue"
            />
          </div>
        </>
      )}

      {activeTab === 'analiticas' && (
        <>
          {topProducts.length > 0 && (
            <>
              <h2 className="fin-section-title">Top productos por ingresos</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Producto</th>
                      <th>Ingresos</th>
                      <th>% del total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.map(([name, rev], i) => (
                      <tr key={name}>
                        <td>{i + 1}</td>
                        <td>{name}</td>
                        <td>{fmt(rev)}</td>
                        <td>{totalRevenue > 0 ? ((rev / totalRevenue) * 100).toFixed(1) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {salesByBrand.length > 1 && (
            <>
              <h2 className="fin-section-title">Ventas por marca</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Marca</th>
                      <th>Ingresos</th>
                      <th>Unidades</th>
                      <th>Costo</th>
                      <th>Ganancia</th>
                      <th>Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesByBrand.map(b => (
                      <tr key={b.brand}>
                        <td>{b.brand}</td>
                        <td>{fmt(b.revenue)}</td>
                        <td>{b.units}</td>
                        <td>{fmt(b.cost)}</td>
                        <td>{fmt(b.profit)}</td>
                        <td>{b.margin.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {inventoryByBrand.length > 1 && (
            <>
              <h2 className="fin-section-title">Inventario por marca</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Marca</th>
                      <th>Productos</th>
                      <th>Stock</th>
                      <th>Valor (costo)</th>
                      <th>Valor (retail)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryByBrand.map(b => (
                      <tr key={b.brand}>
                        <td>{b.brand}</td>
                        <td>{b.products}</td>
                        <td>{b.stock}</td>
                        <td>{fmt(b.costValue)}</td>
                        <td>{fmt(b.retailValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {lowStockProducts.length > 0 && (
            <>
              <h2 className="fin-section-title">Productos con poco stock</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Marca</th>
                      <th>Stock</th>
                      <th>Precio</th>
                      <th>Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockProducts.map(p => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{brandLabel(p.brand)}</td>
                        <td><span className={`fin-stock-badge fin-stock-badge--${p.stock === 0 ? 'red' : 'yellow'}`}>{p.stock}</span></td>
                        <td>{fmt(p.price)}</td>
                        <td>{fmt(p.cost ?? 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {topClients.length > 0 && (
            <>
              <h2 className="fin-section-title">Top clientes</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th>Total comprado</th>
                      <th>Compras</th>
                      <th>Ticket promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topClients.map((c, i) => (
                      <tr key={c.name}>
                        <td>{i + 1}</td>
                        <td>{c.name}</td>
                        <td>{fmt(c.total)}</td>
                        <td>{c.count}</td>
                        <td>{fmt(c.avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {debtClients.length > 0 && (
            <>
              <h2 className="fin-section-title">Clientes con mayor deuda</h2>
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th>Deuda pendiente</th>
                      <th>Ventas con saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debtClients.map((c, i) => (
                      <tr key={c.name}>
                        <td>{i + 1}</td>
                        <td>{c.name}</td>
                        <td style={{ color: '#dc2626', fontWeight: 600 }}>{fmt(c.debt)}</td>
                        <td>{c.sales}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Financials;
